import { useCallback, useMemo, useState } from 'react';
import { MdDragIndicator, MdKeyboardArrowRight } from 'react-icons/md';
import { LuPlus } from 'react-icons/lu';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { AgentApiCandidateFormItemsShow } from '@/types/serializers';
import { Button } from '@/components/Button';
import AddItemDialog from './modals/AddItemDialog';

// title はバックエンドで「〇〇を入力してください」と自動生成されるため、
// サイドバーには短いラベル部分のみを表示する
function getItemLabel(title: string): string {
  return title.replace(/を入力してください$/, '');
}

interface FormItemRowProps {
  item: AgentApiCandidateFormItemsShow;
  isSelected: boolean;
  onClick: () => void;
}

function FormItemRow({ item, isSelected, onClick }: FormItemRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex h-[42px] w-full cursor-pointer items-center gap-xs rounded-sm px-xs shadow-layer1 ${isSelected ? 'bg-primary text-white' : 'bg-white text-main hover:bg-primary_lighten'} ${isDragging ? 'shadow-lg' : ''}`}
      onClick={onClick}
    >
      <div className="flex min-w-0 flex-1 items-center gap-xs">
        <div
          className="flex shrink-0 cursor-grab items-center active:cursor-grabbing"
          {...listeners}
          {...attributes}
        >
          <MdDragIndicator className={`size-5 ${isSelected ? 'text-white' : 'text-sub'}`} />
        </div>
        <p className={`truncate text-heading-xxs ${isSelected ? 'text-white' : 'text-main'}`}>
          {getItemLabel(item.title)}
        </p>
      </div>
      <div className="flex items-center">
        <MdKeyboardArrowRight className={`size-5 ${isSelected ? 'text-white' : 'text-sub'}`} />
      </div>
    </div>
  );
}

interface SidebarProps {
  formItems: AgentApiCandidateFormItemsShow[];
  selectedItemId?: number;
  onItemClick: (itemId: number) => void;
  onReorder?: (reorderedItems: AgentApiCandidateFormItemsShow[]) => void;
  /** 項目追加成功時に呼ばれる（FE で items の最後を選択する用途） */
  onAddSuccess?: () => void;
}

export default function Sidebar({
  formItems,
  selectedItemId,
  onItemClick,
  onReorder,
  onAddSuccess,
}: SidebarProps) {
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));
  const [isAddItemDialogOpen, setIsAddItemDialogOpen] = useState(false);
  const excludedCustomFieldIds = useMemo(
    () => formItems.map((item) => item.custom_field_id).filter((id): id is number => id !== null),
    [formItems]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id && onReorder) {
        const oldIndex = formItems.findIndex((item) => item.id === active.id);
        const newIndex = formItems.findIndex((item) => item.id === over.id);
        const reorderedItems = arrayMove(formItems, oldIndex, newIndex);
        onReorder(reorderedItems);
      }
    },
    [formItems, onReorder]
  );

  const handleOpenAddItemDialog = () => {
    setIsAddItemDialogOpen(true);
  };

  const handleCloseAddItemDialog = () => {
    setIsAddItemDialogOpen(false);
  };

  return (
    <div className="flex h-full w-[188px] shrink-0 flex-col overflow-hidden">
      <div className="mt-xl mb-sm flex shrink-0 items-center justify-between gap-xxs">
        <p className="text-heading-xxs text-sub">表示順</p>
        <Button
          type="button"
          color="secondary"
          size="sm"
          onClick={handleOpenAddItemDialog}
          className="flex items-center gap-xxs"
        >
          <LuPlus className="size-4" />
          <span>追加</span>
        </Button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="hidden-scrollbar h-full overflow-y-auto">
          <div className="flex w-full flex-col px-xxs py-md">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={formItems.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex w-full flex-col gap-xxs">
                  {formItems.map((item) => (
                    <FormItemRow
                      key={item.id}
                      item={item}
                      isSelected={selectedItemId === item.id}
                      onClick={() => onItemClick(item.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>

      <AddItemDialog
        open={isAddItemDialogOpen}
        onClose={handleCloseAddItemDialog}
        excludedCustomFieldIds={excludedCustomFieldIds}
        onAdded={onAddSuccess}
      />
    </div>
  );
}
